import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Check, X, Trash2 } from 'lucide-react';
import {
  checkUsernameAvailable,
  deleteUser,
  fetchOrganisationAssignmentsForProfiles,
  fetchUserById,
  updateUser,
} from '@/lib/api/users';
import { fetchCompanies } from '@/lib/api/index';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { MASTER_TENANT_ID } from '@/types/tenant';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordRecoveryNotice } from '@/components/admin/PasswordRecoveryNotice';
import { Label } from '@/components/ui/label';
import { ImageUpload } from '@/components/ui/image-upload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { OrganisationAccessSummary } from '@/components/admin/OrganisationAccessSummary';

type UserRole = 'admin' | 'company_admin' | 'user';

export default function EditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const { isMasterTenant, tenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  
  // Use domain's tenant_id for filtering (NOT user's profile tenant_id)
  const effectiveTenantId = isMasterTenant ? undefined : tenant?.tenant_id;
  const [formData, setFormData] = useState({
    username: '',
    display_name: '',
    email: '',
    phone: '',
    avatar_url: '',
    role: 'user' as UserRole,
    company_id: undefined as string | undefined,
    job_title: '',
    location: '',
  });

  const [validation, setValidation] = useState({
    usernameChecking: false,
    usernameAvailable: true,
  });

  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [originalUsername, setOriginalUsername] = useState('');
  

  // Fetch user details
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', effectiveTenantId, id],
    queryFn: () => (id ? fetchUserById(id, effectiveTenantId) : null),
    enabled: !!id,
  });

  const {
    data: organisationAssignmentsByProfile = {},
    error: organisationAssignmentsError,
    isLoading: organisationAssignmentsLoading,
  } = useQuery({
    queryKey: ['user-organisation-access', effectiveTenantId, id],
    queryFn: () => fetchOrganisationAssignmentsForProfiles([id as string], effectiveTenantId as string),
    enabled: hasEnterpriseHierarchy && Boolean(id && effectiveTenantId),
  });
  const organisationAssignments = organisationAssignmentsError
    ? undefined
    : id
      ? organisationAssignmentsByProfile[id] || []
      : [];

  // Fetch companies (filtered by DOMAIN's tenant for white-label isolation)
  const { data: companies } = useQuery({
    queryKey: ['companies', effectiveTenantId],
    queryFn: () => fetchCompanies({ tenant_id: effectiveTenantId }),
    enabled: isMasterTenant || !!effectiveTenantId,
  });

  // Load user data into form
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        display_name: user.display_name,
        email: user.email,
        phone: user.phone || '',
        avatar_url: user.avatar_url || '',
        role: user.role as UserRole,
        company_id: user.company_id || undefined,
        job_title: user.job_title || '',
        location: user.location || '',
      });
      setOriginalUsername(user.username);
    }
  }, [user]);

  // Check username availability (debounced) - only if changed
  useEffect(() => {
    if (!formData.username || formData.username === originalUsername || formData.username.length < 3) {
      setValidation((prev) => ({ ...prev, usernameAvailable: true }));
      return;
    }

    setValidation((prev) => ({ ...prev, usernameChecking: true }));
    const timer = setTimeout(async () => {
      const available = await checkUsernameAvailable(formData.username, id);
      setValidation((prev) => ({
        ...prev,
        usernameChecking: false,
        usernameAvailable: available,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username, originalUsername, id]);

  // Role options. The `admin` user_role means different things by tenant:
  //   - Master tenant (MEDD): system-wide Super Administrator
  //   - White-label tenant: Tenant Admin (broad access to this tenant only)
  // `company_admin` is always Branch Admin (scoped to one branch/company).
  const roleOptions = [
    isMasterTenant
      ? {
          value: 'admin' as UserRole,
          label: 'Super Administrator',
          icon: '👑',
          description: 'Full system access — manage all tenants, users, agents, and settings',
          masterOnly: true,
        }
      : {
          value: 'admin' as UserRole,
          label: 'Tenant Admin',
          icon: '👑',
          description: 'Manage users, agents, and simulations across all branches in this tenant',
          masterOnly: false,
        },
    {
      value: 'company_admin' as UserRole,
      label: 'Branch Admin',
      icon: '🏢',
      description: 'Manage users, agents, and simulations within their assigned branch (sub-company)',
      masterOnly: false,
    },
    {
      value: 'user' as UserRole,
      label: 'Standard User',
      icon: '👤',
      description: 'Basic access to assigned simulations',
      masterOnly: false,
    },
  ];

  // White-label/SSO tenants are branch-based: every user must belong to a
  // company/branch. Company Admins always need one too.
  const companyRequired = !isMasterTenant || formData.role === 'company_admin';
  const requiresCompany = companyRequired && !formData.company_id;

  // Type-name-to-confirm delete guard (mirrors the admin user table).
  // EXACT match (only leading/trailing spaces are ignored) — typing the name
  // with a different internal spacing, e.g. "Anthony  Stipanov" (double space),
  // must NOT enable delete. Stored names are clean single-spaced values.
  const [deleteNameInput, setDeleteNameInput] = useState('');
  const confirmName = (user?.display_name ?? '').trim();
  const deleteNameMatches = deleteNameInput.trim() === confirmName && confirmName.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    // Validation
    if (!formData.display_name.trim() || formData.display_name.length < 2) {
      return;
    }

    if (!formData.username.trim() || formData.username.length < 3) {
      return;
    }

    if (!validation.usernameAvailable) {
      return;
    }

    setSubmitting(true);

    try {
      if (hasEnterpriseHierarchy) {
        const enterpriseDb = supabase as any;
        const { error } = await enterpriseDb.rpc('update_enterprise_profile', {
          p_profile_id: id,
          p_display_name: formData.display_name,
          p_job_title: formData.job_title || null,
          p_phone: formData.phone || null,
          p_location: formData.location || null,
          p_avatar_url: formData.avatar_url || null,
        });
        if (error) throw error;

        toast.success('User profile updated successfully');
        if (user?.auth_user_id && user.auth_user_id === currentUser?.supabaseAuthId) {
          await refreshUser();
        }
        navigate('/admin/users');
        return;
      }

      const result = await updateUser(id, {
        username: formData.username,
        display_name: formData.display_name,
        phone: formData.phone || undefined,
        avatar_url: formData.avatar_url || undefined,
        company_id: formData.company_id,
        job_title: formData.job_title || undefined,
        location: formData.location || undefined,
        role: formData.role,
      });

      if (result.success) {
        toast.success('User updated successfully');
        // If the admin edited their OWN account, refresh the session so the
        // header/sidebar name updates immediately — otherwise the saved change
        // looks like it "didn't work" until the next login.
        if (user?.auth_user_id && user.auth_user_id === currentUser?.supabaseAuthId) {
          await refreshUser();
        }
        navigate('/admin/users');
      } else {
        toast.error(result.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    // Guard against accidental deletion: the admin must type the exact name.
    if (!deleteNameMatches) return;

    const result = await deleteUser(id, { enterpriseHierarchy: hasEnterpriseHierarchy });
    if (result.success) {
      navigate('/admin/users');
    }
    setDeleteConfirm(false);
    setDeleteNameInput('');
  };


  if (userLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-foreground">User not found</h2>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Users
        </button>
        <h1 className="text-3xl font-bold text-foreground">Edit User: {user.display_name}</h1>
        <p className="text-muted-foreground mt-1">Update user account and manage roles</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-background border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                required
                value={formData.display_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                placeholder="John Smith"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                disabled
                className="mt-2 bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>

            {/* Username: login credential on master MEDD only. SSO/white-label
                tenants (Boston) sign in via SSO, so the username is derived from
                the email and the field is hidden here too (matches Create User). */}
            {isMasterTenant && (
              <div>
                <Label htmlFor="username">Username *</Label>
                <div className="relative mt-2">
                  <Input
                    id="username"
                    required
                    value={formData.username}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') }))
                    }
                    placeholder="john.smith"
                    className="font-mono"
                  />
                  {validation.usernameChecking && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      Checking...
                    </span>
                  )}
                  {!validation.usernameChecking &&
                    formData.username !== originalUsername &&
                    validation.usernameAvailable && (
                      <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600" size={18} />
                    )}
                  {!validation.usernameChecking &&
                    formData.username !== originalUsername &&
                    !validation.usernameAvailable && (
                      <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-600" size={18} />
                    )}
                </div>
                {!validation.usernameAvailable && formData.username !== originalUsername && (
                  <p className="text-xs text-red-600 mt-1">Username already taken</p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+61 4XX XXX XXX"
                className="mt-2"
              />
            </div>

            {hasEnterpriseHierarchy ? (
              <>
                <div>
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    value={formData.job_title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, job_title: e.target.value }))}
                    placeholder="Territory Manager"
                    className="mt-2"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Organisation position only; this does not grant platform permissions.
                  </p>
                </div>

                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                    placeholder="Sydney, NSW"
                    className="mt-2"
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Role & Company */}
        {hasEnterpriseHierarchy ? (
          <div className="rounded-xl border border-border bg-background p-6">
            <h2 className="text-xl font-bold text-foreground">Organisation Access</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Platform permissions are independent of the job title above. Branch, unit and role assignments remain explicit and auditable.
            </p>
            <div className="mt-4">
              {organisationAssignmentsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : (
                <OrganisationAccessSummary assignments={organisationAssignments} />
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={() => {
                const returnTo = encodeURIComponent(`/admin/users/${id}/edit`);
                navigate(`/admin/organisation?tab=people&person=${id}&action=role&returnTo=${returnTo}`);
              }}
            >
              Change role or unit
            </Button>
          </div>
        ) : <>
        <div className="bg-background border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Role & Permissions</h2>
          <div className="grid grid-cols-1 gap-4 mb-6">
            {roleOptions.map((role) => (
              <button
                key={role.value}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, role: role.value }))}
                className={`p-4 border-2 rounded-xl transition-all text-left ${
                  formData.role === role.value
                    ? 'border-primary bg-primary/5'
                    : role.masterOnly
                      ? 'border-amber-500/50 bg-amber-500/5 hover:border-amber-500'
                      : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{role.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground flex items-center gap-2">
                      {role.label}
                      {role.masterOnly && (
                        <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full font-medium">
                          System Level
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{role.description}</div>
                  </div>
                  {formData.role === role.value && <CheckCircle className="text-primary" size={24} />}
                </div>
              </button>
            ))}
          </div>

          {requiresCompany && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠️ {isMasterTenant ? 'Company Admin role requires a company to be selected' : 'Please select the branch this user belongs to'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="company">
                {isMasterTenant ? 'Company' : 'Branch'} {companyRequired && <span className="text-destructive">*</span>}
              </Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData((prev) => ({ ...prev, company_id: value }))}>
                <SelectTrigger className={`mt-2 ${requiresCompany ? 'border-amber-500' : ''}`}>
                  <SelectValue placeholder={isMasterTenant ? 'None (Independent)' : 'Select a branch'} />
                </SelectTrigger>
                <SelectContent>
                  {companies?.map((company: any) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => setFormData((prev) => ({ ...prev, job_title: e.target.value }))}
                placeholder="Medical Sales Representative"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="Sydney, NSW"
                className="mt-2"
              />
            </div>
          </div>
        </div>
        </>}

        {/* Avatar */}
        <div className="bg-background border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Avatar</h2>
          <ImageUpload
            value={formData.avatar_url || ''}
            onChange={(url) => setFormData((prev) => ({ ...prev, avatar_url: url }))}
            pathPrefix="avatars"
            placeholder="user"
            shape="circle"
            label="Profile Photo"
          />
        </div>

        {/* Manual resets stay unavailable during the reviewed SSO containment interval. */}
        {isMasterTenant && user?.auth_user_id && <PasswordRecoveryNotice />}

        {/* Delete Section */}
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-destructive mb-2">Danger Zone</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {hasEnterpriseHierarchy
              ? 'Remove this user from the organisation access you manage. Their other access and account history will be kept.'
              : 'Once you delete a user, there is no going back. Please be certain.'}
          </p>
          <Button type="button" variant="destructive" onClick={() => setDeleteConfirm(true)} className="gap-2">
            <Trash2 size={18} />
            {hasEnterpriseHierarchy ? 'Remove User' : 'Delete User'}
          </Button>
        </div>

        {/* Form Actions */}
        <div className="flex gap-4">
          <Button
            type="submit"
            className="flex-1"
            disabled={submitting || !validation.usernameAvailable || validation.usernameChecking || requiresCompany}
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/users')} className="flex-1">
            Cancel
          </Button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-2xl">
                {hasEnterpriseHierarchy ? 'Remove User?' : 'Delete User?'}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              {hasEnterpriseHierarchy ? (
                <>
                  Remove <span className="font-semibold text-foreground">{confirmName}</span> from the organisation access
                  you manage? Their other organisation access and account history will be kept.
                </>
              ) : (
                <>
                  Are you sure you want to delete{' '}
                  <span className="font-semibold text-foreground">{confirmName}</span>? This action cannot be
                  undone and will permanently remove all associated data. Consider{' '}
                  <span className="font-medium">deactivating</span> the user instead — it locks them out but keeps their history.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Label htmlFor="delete_confirm_name" className="text-sm">
              Type <span className="font-semibold">{confirmName}</span> to confirm:
            </Label>
            <Input
              id="delete_confirm_name"
              value={deleteNameInput}
              onChange={(e) => setDeleteNameInput(e.target.value)}
              placeholder={confirmName}
              className="mt-2"
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirm(false); setDeleteNameInput(''); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!deleteNameMatches}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:pointer-events-none"
            >
              {hasEnterpriseHierarchy ? 'Remove User' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
